import { useEffect, useMemo, useRef, type FunctionComponent } from "react";
import { MinusCircleOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Switch,
  Tabs,
  Typography,
} from "antd";
import isEqual from "lodash.isequal";
import round from "lodash.round";
import { Trans, useTranslation } from "react-i18next";

import { productActions, productAutomationActions } from "@fitness-app/app-store";
import {
  DEFAULT_TPAY_INSTALLMENTS_MIN_AMOUNT,
  TPAY_INSTALLMENT_CHANNELS,
} from "@fitness-app/data-models/contants/tpayInstallments";
import {
  LastNameFieldValues,
  type ProductCartSettings,
  type ProductInvoiceConfig,
  type ProductTaxSettings,
} from "@fitness-app/data-models/entities/Automation";
import {
  PaymentClient,
  ProductChargeType,
  ProductType,
  type PaymentClientEnum,
} from "@fitness-app/data-models/entities/Product";
import { DEFAULT_INCOME_FLAT_TAX_RATES, DEFAULT_VAT_RATES } from "@fitness-app/utils/src/taxes/taxRates";

import UploadField from "~/components/UploadField/UploadField";
import { useEntityChange } from "~/hooks/useEntityChange";
import { useSideTabsPosition } from "~/hooks/useIsMobile";
import { useQuery } from "~/hooks/useQuery";
import i18next from "~/i18nextConfig";
import { useMetaConversion } from "~/modules/Products/Product/ProductSettings/hooks/useMetaConversion";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import {
  getPaymentMethodsList,
  getPaymentMethodsMap,
  PaymentMethodsField,
} from "../ProductDetails/components/PaymentMethodsField";
import { usePaymentGateways } from "../ProductDetails/hooks/usePaymentGateways";

interface OwnProps {
  productId: string;
}

type Props = OwnProps;

interface AutomationSettingsForm {
  successUrl: string | null;
  showPromotionCodesField: CouponCodeField;
  invoiceAsOption: boolean;
  disableEmailNotificationAboutPurchase: boolean;
  disableEmailNotificationAboutPurchaseToOwner: boolean;
  invoiceConfig: ProductInvoiceConfig;
  purchaseConfirmationAttachments: { url: string; name: string; uid: string; contentType: string }[];
  facebookPixelId: string | null;
  googleAnalyticsId: string | null;
  googleAnalyticsGtmId: string | null;
  hideCountryField: boolean;
  showTermsPP: boolean;
  termsUrl: string | null;
  privacyPolicyUrl: string | null;
  claim: string | null;
  showAdditionalInfoField: AdditionalInfoField;
  showPhoneField: PhoneNumberFieldValues;
  showLastNameField: LastNameFieldValues;
  metaConversionIntegrationId: string | null;
  requireFullClientData: boolean;
  sendInvoiceToClient: boolean;
  billingEmail: string | null;
  contactEmail: string | null;
  enabledPaymentGateways: PaymentClientEnum[];
  paymentsMethods: ("p24" | "blik" | "klarna")[];
  disableMainCheckoutSales: boolean;
  cart: CartSettingsForm;
}

/**
 * Cart settings as the form holds them. Identical to what is stored except for
 * the installment bounds: the admin types PLN, the settings keep grosze like
 * every other amount in the payment path. Converted at both boundaries by
 * {@link groszeToZloty} / {@link zlotyToGrosze}.
 */
interface CartSettingsForm extends Omit<
  ProductCartSettings,
  "tpayInstallmentsMinAmount" | "tpayInstallmentsMaxAmount"
> {
  tpayInstallmentsMinAmount?: number | null;
  tpayInstallmentsMaxAmount?: number | null;
}

const groszeToZloty = (amount?: number | null): number | null => (typeof amount === "number" ? amount / 100 : null);

const zlotyToGrosze = (amount?: number | null): number | null =>
  typeof amount === "number" ? Math.round(amount * 100) : null;

enum CouponCodeField {
  Hidden = "hidden",
  ShowForAll = "showForAll",
  ShowOnlyForSubscription = "showOnlyForSubscription",
}

enum AdditionalInfoField {
  Hidden = "hidden",
  ShowOptional = "showOptional",
  ShowRequired = "showRequired",
}

enum PhoneNumberFieldValues {
  Hidden = "hidden",
  ShowOptional = "showOptional",
  ShowRequired = "showRequired",
}

export const flatTaxRates = DEFAULT_INCOME_FLAT_TAX_RATES.map((rate) => ({
  value: rate,
  label: `${round(rate * 100, 1)}%`,
}));

export const VAT_RATES = [
  {
    value: DEFAULT_VAT_RATES[0],
    label: i18next.t("common:vatRate.zw"),
  },
  {
    value: DEFAULT_VAT_RATES[1],
    label: i18next.t("common:vatRate.np"),
  },
  ...DEFAULT_VAT_RATES.slice(2).map((rate) => ({
    value: rate,
    label: `${rate}%`,
  })),
];

const ProductSettings: FunctionComponent<Props> = ({ productId }) => {
  const sideTabsPosition = useSideTabsPosition();
  const { t } = useTranslation(["products", "common"]);

  const npTaxKindOptions = useMemo(
    () => [
      {
        value: "export_service",
        label: t("products:productAutomation.npTaxKind.export_service"),
      },
      {
        value: "export_service_eu",
        label: t("products:productAutomation.npTaxKind.export_service_eu"),
      },
    ],
    [t],
  );
  const [formController] = Form.useForm<AutomationSettingsForm>();
  const dispatch = useAppDispatch();
  const productDetails = useAppSelector((store) => store.product.details);
  const productAutomation = useAppSelector((store) => store.productAutomation.data);
  const { data } = useAppSelector((state) => state.productsSettings);
  const [loading, onSuccess, onFailure, onStart] = useEntityChange();
  const metaConversionIntegrationId = Form.useWatch("metaConversionIntegrationId", formController);
  const facebookPixelIdValue = Form.useWatch("facebookPixelId", formController);
  const invoiceAsOption = Form.useWatch(["invoiceAsOption"], formController);
  const vatRate = Form.useWatch(
    ["invoiceConfig", "taxSettings", "vatRate"],
    formController,
  ) as ProductTaxSettings["vatRate"];
  const showTermsPP = Form.useWatch("showTermsPP", formController);
  const disableMainCheckoutSales = Form.useWatch("disableMainCheckoutSales", formController);
  const showTPayInstallments = Form.useWatch(["cart", "showTPayInstallments"], formController);
  const installmentChannelIds = Form.useWatch(["cart", "tpayInstallmentsChannelIds"], formController);
  const { conversionsListOptions, getPixelIdForIntegration } = useMetaConversion();
  const query = useQuery();
  const { activePaymentGatewaysOptions } = usePaymentGateways();

  const tabFromUrl = query.get("tab");

  // Label overrides are rendered per selected channel, in the order of the constant
  // so the list does not jump around as the admin ticks boxes.
  const selectedInstallmentChannels = useMemo(
    () => TPAY_INSTALLMENT_CHANNELS.filter((channel) => installmentChannelIds?.includes(channel.channelId)),
    [installmentChannelIds],
  );

  const cartData = data?.checkoutSettings?.cart;

  useEffect(() => {
    if (showTermsPP) {
      formController?.setFieldsValue({
        termsUrl: productAutomation?.settings?.termsUrl ?? cartData?.pl?.termsUrl ?? null,
        privacyPolicyUrl: productAutomation?.settings?.privacyPolicyUrl ?? cartData?.pl?.privacyPolicyUrl ?? null,
        claim: productAutomation?.settings?.claim ?? cartData?.pl?.claim ?? null,
      });
    }
  }, [
    showTermsPP,
    formController,
    cartData?.pl?.termsUrl,
    cartData?.pl?.privacyPolicyUrl,
    cartData?.pl?.claim,
    productAutomation?.settings,
  ]);

  const onSubmit = (data: AutomationSettingsForm) => {
    onStart();
    if (data.invoiceConfig) {
      data.invoiceConfig = {
        ...data.invoiceConfig,
        flatTaxRate: data.invoiceConfig.flatTaxRate || null,
        itemName: data.invoiceConfig.itemName || null,
      };
    }

    if (data.invoiceConfig.taxSettings && !data.invoiceConfig.taxSettings.vatRate) {
      data.invoiceConfig.taxSettings = null;
    }

    // The blocks below mutate taxSettings, so they must only run when it is still
    // present — the guard above may have reset it to null (no vatRate selected),
    // and assigning onto null would throw a TypeError.
    if (data.invoiceConfig.taxSettings) {
      if (
        data.invoiceConfig.taxSettings.legalVatExemption &&
        typeof data.invoiceConfig.taxSettings.vatRate === "number"
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- form types are not ideal
        (data.invoiceConfig.taxSettings as any).legalVatExemption = "";
      }

      if (data.invoiceConfig.taxSettings.vatRate !== "zw") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- form types are not ideal
        (data.invoiceConfig.taxSettings as any).legalVatExemption = undefined;
      }

      if (data.invoiceConfig.taxSettings.vatRate !== "np") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- form types are not ideal
        (data.invoiceConfig.taxSettings as any).npTaxKind = undefined;
      }
    }

    if (data.invoiceConfig.pkwiu === undefined) {
      delete data.invoiceConfig.pkwiu;
    }

    if (data.invoiceConfig.gtu === undefined) {
      delete data.invoiceConfig.gtu;
    }

    const {
      showPromotionCodesField,
      showAdditionalInfoField,
      showPhoneField,
      showLastNameField,
      billingEmail,
      contactEmail,
      enabledPaymentGateways,
      paymentsMethods,
      ...rest
    } = data;

    if (
      !isEqual(enabledPaymentGateways, productDetails?.enabledPaymentGateways) ||
      !isEqual(getPaymentMethodsList(productDetails?.paymentsMethods), paymentsMethods)
    ) {
      const updatedPaymentsMethods = getPaymentMethodsMap(paymentsMethods);

      dispatch(
        productActions.updateProductPaymentGateways({
          payload: {
            productId,
            enabledPaymentGateways,
            paymentsMethods: !isEqual(getPaymentMethodsList(productDetails?.paymentsMethods), paymentsMethods)
              ? updatedPaymentsMethods
              : undefined,
          },
        }),
      );
    }

    void dispatch(
      productAutomationActions.updateProductAutomationSettings({
        payload: {
          productId,
          settings: {
            ...rest,
            cart: {
              ...rest.cart,
              tpayInstallmentsMinAmount: zlotyToGrosze(rest.cart?.tpayInstallmentsMinAmount),
              tpayInstallmentsMaxAmount: zlotyToGrosze(rest.cart?.tpayInstallmentsMaxAmount),
            },
            showPromotionCodesField: showPromotionCodesField === CouponCodeField.ShowForAll,
            showPromotionCodesFieldOnlyForSubscription:
              showPromotionCodesField === CouponCodeField.ShowOnlyForSubscription,
            showAdditionalInfoOptionalField: showAdditionalInfoField === AdditionalInfoField.ShowOptional,
            showAdditionalInfoRequiredField: showAdditionalInfoField === AdditionalInfoField.ShowRequired,
            termsUrl: showTermsPP ? (data.termsUrl ?? null) : null,
            privacyPolicyUrl: showTermsPP ? (data.privacyPolicyUrl ?? null) : null,
            claim: showTermsPP ? (data.claim ?? null) : null,
            showPhoneOptionalField: showPhoneField === PhoneNumberFieldValues.ShowOptional,
            showPhoneField: null, // it's added to distinguish old saved settings from new one
            showPhoneRequiredField: showPhoneField === PhoneNumberFieldValues.ShowRequired,
            showLastNameField: !showLastNameField ? LastNameFieldValues.ShowRequired : showLastNameField,
          },
          billingEmail,
          contactEmail,
        },
        onSuccess,
        onFailure,
      }),
    );
  };

  // Applied once per product, and only once the <Form> is mounted (it renders
  // behind the `productDetails` gate below — setFieldsValue on an unmounted
  // form is a silent no-op). Re-running on every store change is destructive:
  // backend services (invoices, automation tasks) update product_automation in
  // the background, the realtime subscription refreshes the store, and each
  // refresh would overwrite the admin's in-progress edits — most visibly the
  // installment channels select, which the `?? []` fallback resets outright.
  const appliedAutomationProductId = useRef<string | null>(null);

  useEffect(() => {
    if (productAutomation?.settings && productDetails) {
      if (appliedAutomationProductId.current === productAutomation.productId) {
        return;
      }
      appliedAutomationProductId.current = productAutomation.productId;

      const showPromotionCodesField = productAutomation.settings?.showPromotionCodesFieldOnlyForSubscription
        ? CouponCodeField.ShowOnlyForSubscription
        : productAutomation.settings?.showPromotionCodesField
          ? CouponCodeField.ShowForAll
          : CouponCodeField.Hidden;

      const showAdditionalInfoField = productAutomation.settings?.showAdditionalInfoRequiredField
        ? AdditionalInfoField.ShowRequired
        : productAutomation.settings?.showAdditionalInfoOptionalField
          ? AdditionalInfoField.ShowOptional
          : AdditionalInfoField.Hidden;
      const showTerms =
        Boolean(productAutomation?.settings?.termsUrl) ||
        Boolean(productAutomation?.settings?.privacyPolicyUrl) ||
        Boolean(productAutomation?.settings?.claim);

      const showPhoneField =
        productAutomation?.settings?.showPhoneOptionalField || productAutomation?.settings?.showPhoneField === true
          ? PhoneNumberFieldValues.ShowOptional
          : productAutomation?.settings?.showPhoneRequiredField
            ? PhoneNumberFieldValues.ShowRequired
            : PhoneNumberFieldValues.Hidden;

      formController?.setFieldsValue({
        ...productAutomation.settings,
        billingEmail: productAutomation.billingEmail,
        contactEmail: productAutomation.contactEmail,
        showPromotionCodesField,
        showAdditionalInfoField,
        showPhoneField,
        showTermsPP: showTerms,
        enabledPaymentGateways: productDetails?.enabledPaymentGateways || [PaymentClient.STRIPE, PaymentClient.TPAY],
        requireFullClientData: productAutomation?.settings?.requireFullClientData ?? false,
        disableMainCheckoutSales: productAutomation?.settings?.disableMainCheckoutSales ?? false,
        showLastNameField: productAutomation.settings?.showLastNameField || LastNameFieldValues.ShowRequired,
        cart: {
          ...productAutomation.settings?.cart,
          tpayInstallmentsChannelIds: productAutomation.settings?.cart?.tpayInstallmentsChannelIds ?? [],
          tpayInstallmentsMinAmount:
            groszeToZloty(productAutomation.settings?.cart?.tpayInstallmentsMinAmount) ??
            groszeToZloty(DEFAULT_TPAY_INSTALLMENTS_MIN_AMOUNT),
          tpayInstallmentsMaxAmount: groszeToZloty(productAutomation.settings?.cart?.tpayInstallmentsMaxAmount),
        },
      });
    }
  }, [productAutomation, productDetails]);

  const inheritedPixelIdFromCapi = getPixelIdForIntegration(metaConversionIntegrationId);
  const effectivePixelId = facebookPixelIdValue || inheritedPixelIdFromCapi;
  const isPixelInheritedFromCapi = !facebookPixelIdValue && Boolean(inheritedPixelIdFromCapi);

  const gtuCodes = [
    {
      value: null,
      label: t("products:productAutomation.productGtuCodeNone"),
    },
    ...Array.from({ length: 13 }, (_, i) => `GTU_${(i + 1).toString().padStart(2, "0")}`).map((code) => ({
      value: code,
      label: code,
    })),
  ];

  if (!productDetails) {
    return null;
  }

  const hiddenField = [ProductType.ONLY_CONTENT, ProductType.ADDON].includes(productDetails.type);

  return (
    <Card
      extra={
        <Space>
          <Button loading={!!loading} icon={<SaveOutlined />} type="primary" onClick={() => formController?.submit()}>
            {t<string>("common:button.save")}
          </Button>
        </Space>
      }
    >
      <Form<AutomationSettingsForm>
        name="active-campaign-form"
        layout="vertical"
        form={formController}
        onFinish={onSubmit}
        validateTrigger={["onSubmit"]}
        initialValues={{
          successUrl: "",
          showPromotionCodesField: CouponCodeField.Hidden,
          showPhoneField: PhoneNumberFieldValues.Hidden,
          showAdditionalInfoField: AdditionalInfoField.Hidden,
          purchaseConfirmationAttachments: productAutomation?.settings?.purchaseConfirmationAttachments ?? [],
          showTermsPP: false,
          termsUrl: null,
          privacyPolicyUrl: null,
          claim: null,
          allowDowngradeSubscriptionPlan: false,
          invoiceAsOption: false,
          disableEmailNotificationAboutPurchase: false,
          allowUpgradeSubscriptionPlan: false,
          issueReceiptWhenInvoiceIsOptIn: false,
          issueInvoiceToReceiptForIndividuals: false,
          disableMainCheckoutSales: false,
          allowDowngradeSubscriptionPlanDesc: false,
          disableEmailNotificationAboutPurchaseToOwner: false,
          googleAnalyticsId: null,
          facebookPixelId: null,
          metaConversionIntegrationId: null,
          invoiceConfig: {
            flatTaxRate: null,
            itemName: null,
          },
          cart: {
            features: [],
            preSaleUrl: "",
            customButtonLabel: "",
            hidePriceWhenPreSaleLinkExists: false,
            useTPayBlik: false,
            useTPayBlikRecurring: false,
            installmentsContractFraming: false,
            showTPayPayPoAsPaymentMethod: false,
            showTPayPayPalAsPaymentMethod: false,
            showTPayInstallments: false,
            tpayInstallmentsChannelIds: [],
            tpayInstallmentsMinAmount: groszeToZloty(DEFAULT_TPAY_INSTALLMENTS_MIN_AMOUNT),
            tpayInstallmentsMaxAmount: null,
          },
          contactEmail: data?.contactEmail ?? null,
          billingEmail: data?.billingEmail ?? null,
          enabledPaymentGateways: productDetails?.enabledPaymentGateways || [PaymentClient.STRIPE, PaymentClient.TPAY],
          paymentsMethods: getPaymentMethodsList(productDetails?.paymentsMethods),
        }}
      >
        <Tabs tabPosition={sideTabsPosition} defaultActiveKey={tabFromUrl || "afterPurchase"}>
          <Tabs.TabPane key="afterPurchase" forceRender tab={t<string>("productAutomation.afterPurchase")}>
            <Card bordered={false}>
              <Form.Item
                name="successUrl"
                label={t(
                  productDetails?.chargeType === ProductChargeType.FREE
                    ? "productAutomation.successUrlFree"
                    : "productAutomation.successUrl",
                )}
                tooltip={t("productAutomation.successUrlTooltip")}
                rules={[
                  {
                    type: "url",
                    message: t<string>("common:validationErrors.wrongUrl"),
                  },
                ]}
              >
                <Input autoFocus placeholder={t<string>("productAutomation.successUrlPlaceholder")} />
              </Form.Item>
            </Card>
          </Tabs.TabPane>
          <Tabs.TabPane key="emailNotifications" forceRender tab={t<string>("productAutomation.emailNotifications")}>
            <Card bordered={false}>
              <Form.Item
                name="disableEmailNotificationAboutPurchase"
                valuePropName="checked"
                label={t("productAutomation.emailNotifications")}
              >
                <Checkbox>
                  <Trans t={t} i18nKey="productAutomation.disableEmailNotificationAboutPurchase" />
                </Checkbox>
              </Form.Item>

              <Form.Item
                tooltip={t<string>("products:form.fileAdditionalInfo")}
                name="purchaseConfirmationAttachments"
                label={t<string>("productAutomation.attachmentsForConfirmationEmail")}
                valuePropName="fileList"
                getValueFromEvent={(e: unknown) => e}
                wrapperCol={{ span: 12 }}
              >
                <UploadField
                  multiple
                  withContentType
                  bucket="users"
                  fileList={productAutomation?.settings?.purchaseConfirmationAttachments}
                  storageRef={`${productDetails.owner}/products/${productId}/resources`}
                />
              </Form.Item>

              {productAutomation?.settings?.disableEmailNotificationAboutPurchase && (
                <Alert
                  type="warning"
                  message={t("productAutomation.disableEmailNotificationAboutPurchaseInfo")}
                  style={{ margin: "-10px 0 20px" }}
                />
              )}

              <Form.Item name="disableEmailNotificationAboutPurchaseToOwner" valuePropName="checked">
                <Checkbox>
                  <Trans t={t} i18nKey="productAutomation.disableEmailNotificationAboutPurchaseToOwner" />
                </Checkbox>
              </Form.Item>
            </Card>
          </Tabs.TabPane>

          {productDetails?.chargeType !== ProductChargeType.FREE && (
            <>
              <Tabs.TabPane key="promotionCodes" forceRender tab={t<string>("productAutomation.promotionCodes")}>
                <Card bordered={false}>
                  <Form.Item
                    name="showPromotionCodesField"
                    label={
                      <Typography.Title level={5} style={{ marginBottom: 0 }}>
                        {t("productAutomation.promotionCodes")}
                      </Typography.Title>
                    }
                    tooltip={t("productAutomation.showPromotionCodesTooltip")}
                  >
                    <Radio.Group
                      style={{
                        flexDirection: "column",
                        display: "flex",
                        marginTop: "0.5rem",
                      }}
                    >
                      <Radio value={CouponCodeField.Hidden}>{t("productAutomation.hidePromotionCodesField")}</Radio>
                      <Radio value={CouponCodeField.ShowForAll}>{t("productAutomation.showPromotionCodesField")}</Radio>
                      <Radio value={CouponCodeField.ShowOnlyForSubscription}>
                        {t("productAutomation.showPromotionCodesOnlyForSubscriptionField")}
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                </Card>
              </Tabs.TabPane>
              <Tabs.TabPane key="invoices" forceRender tab={t<string>("productAutomation.invoices")}>
                <Card bordered={false}>
                  <Form.Item
                    name="invoiceAsOption"
                    valuePropName="checked"
                    label={<Typography.Title level={5}>{t("productAutomation.invoices")}</Typography.Title>}
                  >
                    <Checkbox>{t("productAutomation.invoiceAsOptionField")}</Checkbox>
                  </Form.Item>

                  <Form.Item name="sendInvoiceToClient" valuePropName="checked">
                    <Checkbox>{t("productAutomation.sendInvoiceToClient")}</Checkbox>
                  </Form.Item>

                  <Alert message={t("productAutomation.invoiceAsOptionInfo")} style={{ margin: "-10px 0 20px" }} />

                  {invoiceAsOption && (
                    <Form.Item name="issueReceiptWhenInvoiceIsOptIn" valuePropName="checked">
                      <Checkbox>{t("productAutomation.issueReceiptWhenInvoiceIsOptIn")}</Checkbox>
                    </Form.Item>
                  )}

                  {invoiceAsOption && (
                    <Form.Item name="requireFullClientData" valuePropName="checked">
                      <Checkbox>{t("productAutomation.requireFullClientData")}</Checkbox>
                    </Form.Item>
                  )}

                  <Form.Item name="issueInvoiceToReceiptForIndividuals" valuePropName="checked">
                    <Checkbox>{t("productAutomation.issueInvoiceToReceiptForIndividuals")}</Checkbox>
                  </Form.Item>

                  <Alert
                    message={t("productAutomation.issueInvoiceToReceiptForIndividualsInfo")}
                    style={{ margin: "-10px 0 20px" }}
                  />

                  <Form.Item name={["invoiceConfig", "flatTaxRate"]} label={t("productAutomation.flatTaxRate")}>
                    <Select
                      options={flatTaxRates}
                      allowClear
                      placeholder={t("productAutomation.flatTaxRatePlaceholder")}
                    />
                  </Form.Item>

                  <Form.Item
                    name={["invoiceConfig", "taxSettings", "vatRate"]}
                    label={t("productAutomation.productVatRate")}
                  >
                    <Select
                      options={VAT_RATES}
                      allowClear
                      placeholder={t("productAutomation.productVatRatePlaceholder")}
                    />
                  </Form.Item>

                  {vatRate === "zw" ? (
                    <Form.Item
                      name={["invoiceConfig", "taxSettings", "legalVatExemption"]}
                      label={t("productAutomation.legalVatExemption")}
                      rules={[
                        {
                          required: vatRate === "zw",
                          message: t<string>("common:validationErrors.fieldIsRequired"),
                        },
                        {
                          max: 50,
                          message: t<string>("common:validationErrors.maxLength", {
                            number: 50,
                          }),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  ) : null}

                  {vatRate === "np" ? (
                    <Form.Item
                      name={["invoiceConfig", "taxSettings", "npTaxKind"]}
                      label={t("productAutomation.npTaxKind.label")}
                      rules={[
                        {
                          required: true,
                          message: t<string>("common:validationErrors.fieldIsRequired"),
                        },
                      ]}
                    >
                      <Select
                        options={npTaxKindOptions}
                        placeholder={t<string>("productAutomation.npTaxKind.placeholder")}
                      />
                    </Form.Item>
                  ) : null}

                  <Form.Item
                    name={["invoiceConfig", "pkwiu"]}
                    label={t("productAutomation.productPkwiuCode")}
                    rules={[
                      {
                        required: vatRate === "zw",
                        message: t<string>("common:validationErrors.fieldIsRequired"),
                      },
                    ]}
                  >
                    <Input placeholder={t<string>("productAutomation.productPkwiuCodePlaceholder")} />
                  </Form.Item>
                  <Form.Item name={["invoiceConfig", "gtu"]} label={t("productAutomation.productGtuCode")}>
                    <Select
                      options={gtuCodes}
                      defaultValue={gtuCodes[0]?.value}
                      placeholder={t("productAutomation.productGtuCodePlaceholder")}
                    />
                  </Form.Item>
                </Card>
              </Tabs.TabPane>
              <Tabs.TabPane
                key="analyticsInCheckout"
                forceRender
                tab={t<string>("productAutomation.analyticsInCheckout")}
              >
                <Card bordered={false}>
                  <Form.Item name="googleAnalyticsGtmId" label={t<string>("productAutomation.googleAnalyticsGtmId")}>
                    <Input placeholder={t<string>("productAutomation.googleAnalyticsGtmIdPlaceholder")} />
                  </Form.Item>

                  <Form.Item name="googleAnalyticsId" label={t("productAutomation.googleAnalyticsId")}>
                    <Input placeholder={t<string>("productAutomation.googleAnalyticsIdPlaceholder")} />
                  </Form.Item>

                  <Form.Item name="facebookPixelId" label={t("productAutomation.facebookPixelId")}>
                    <Input placeholder={t<string>("productAutomation.facebookPixelIdPlaceholder")} />
                  </Form.Item>

                  {isPixelInheritedFromCapi && (
                    <Form.Item>
                      <Alert
                        type="info"
                        message={t<string>("productAutomation.facebookPixelIdInheritedFromCapi", {
                          pixelId: inheritedPixelIdFromCapi,
                        })}
                      />
                    </Form.Item>
                  )}
                  <Form.Item
                    name="metaConversionIntegrationId"
                    label={t<string>("productAutomation.metaConversionIntegration")}
                  >
                    <Select
                      options={conversionsListOptions}
                      placeholder={t<string>("productAutomation.metaConversionIntegrationPlaceholder")}
                      allowClear
                    />
                  </Form.Item>
                  {effectivePixelId && metaConversionIntegrationId && (
                    <Form.Item>
                      <Alert
                        type="success"
                        message={t<string>("productAutomation.pixelAndCapiActive", {
                          pixelId: effectivePixelId,
                        })}
                      />
                    </Form.Item>
                  )}
                </Card>
              </Tabs.TabPane>
              <Tabs.TabPane key="subscription" forceRender tab={t<string>("productAutomation.subscription")}>
                <Card bordered={false}>
                  <Form.Item name="allowUpgradeSubscriptionPlan" valuePropName="checked" noStyle>
                    <Checkbox>{t("productAutomation.allowUpgradeSubscriptionPlan")}</Checkbox>
                  </Form.Item>

                  <Alert className="my-4" message={t("productAutomation.allowUpgradeSubscriptionPlanDesc")} />

                  <Form.Item name="allowDowngradeSubscriptionPlan" valuePropName="checked" noStyle>
                    <Checkbox>{t("productAutomation.allowDowngradeSubscriptionPlan")}</Checkbox>
                  </Form.Item>
                </Card>
              </Tabs.TabPane>
              <Tabs.TabPane key="checkout" forceRender tab={t<string>("productAutomation.checkout")}>
                <Card bordered={false}>
                  <Typography.Title level={5}>{t("productDetails.paymentGatewaysSettings")}</Typography.Title>

                  <Form.Item
                    name="enabledPaymentGateways"
                    label={t("productDetails.enabledPaymentGateways")}
                    rules={[
                      {
                        required: true,
                        message: t<string>("products:productDetails.selectAtLeastOnePaymentGateway"),
                        type: "array",
                        min: 1,
                      },
                    ]}
                  >
                    <Select mode="multiple" className="max-w-[350px]" options={activePaymentGatewaysOptions} />
                  </Form.Item>

                  <PaymentMethodsField form={formController} />

                  <Typography.Title level={5}>{t("productAutomation.checkoutSettings")}</Typography.Title>

                  <Form.Item
                    name="disableMainCheckoutSales"
                    label={t("productAutomation.disableMainCheckoutSales")}
                    tooltip={t("productAutomation.disableMainCheckoutSalesTooltip")}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  {disableMainCheckoutSales && (
                    <Alert
                      type="warning"
                      message={t("productAutomation.disableMainCheckoutSalesInfo")}
                      style={{ margin: "-10px 0 20px" }}
                    />
                  )}

                  <Form.Item
                    name="contactEmail"
                    label={t("settings:checkout.contactEmail")}
                    rules={[
                      {
                        type: "email",
                        message: t<string>("common:validationErrors.wrongEmail"),
                      },
                    ]}
                  >
                    <Input placeholder={t<string>("settings:checkout.optional")} />
                  </Form.Item>

                  <Form.Item
                    name="billingEmail"
                    label={t("settings:checkout.billingEmail")}
                    rules={[
                      {
                        type: "email",
                        message: t<string>("common:validationErrors.wrongEmail"),
                      },
                    ]}
                  >
                    <Input placeholder={t<string>("settings:checkout.optional")} />
                  </Form.Item>

                  <Form.Item
                    name={["cart", "preSaleUrl"]}
                    label={t("productAutomation.preSaleUrl")}
                    rules={[
                      {
                        type: "url",
                        message: t<string>("common:validationErrors.wrongUrl"),
                      },
                    ]}
                  >
                    <Input placeholder={t<string>("productAutomation.preSaleUrlPlaceholder")} />
                  </Form.Item>

                  <Form.Item label={t("productAutomation.cartFeatures")} hidden={hiddenField}>
                    <Form.List name={["cart", "features"]} initialValue={[""]}>
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field) => (
                            <div
                              key={field.key}
                              style={{
                                display: "flex",
                                width: "100%",
                                alignItems: "baseline",
                              }}
                            >
                              <Form.Item
                                {...field}
                                name={[field.name]}
                                key={field.key}
                                rules={[
                                  {
                                    required: true,
                                    message: t<string>("common:validationErrors.fieldIsRequired"),
                                  },
                                ]}
                                style={{ width: "90%" }}
                              >
                                <Input placeholder={t<string>("productAutomation.cartFeaturesPlaceholder")} />
                              </Form.Item>
                              <MinusCircleOutlined onClick={() => remove(field.name)} style={{ marginLeft: 12 }} />
                            </div>
                          ))}
                          {fields.length < 5 && (
                            <Form.Item>
                              <Button
                                type="dashed"
                                onClick={() => add()}
                                block
                                icon={<PlusOutlined />}
                                style={{ marginTop: 10 }}
                              >
                                {t("common:button.add")}
                              </Button>
                            </Form.Item>
                          )}
                        </>
                      )}
                    </Form.List>
                  </Form.Item>

                  <Form.Item
                    hidden={hiddenField}
                    name={["cart", "preSaleUrl"]}
                    label={t("productAutomation.preSaleUrl")}
                    rules={[
                      {
                        type: "url",
                        message: t<string>("common:validationErrors.wrongUrl"),
                      },
                    ]}
                  >
                    <Input placeholder={t<string>("productAutomation.preSaleUrlPlaceholder")} />
                  </Form.Item>

                  <Form.Item
                    hidden={hiddenField}
                    name={["cart", "hidePriceWhenPreSaleLinkExists"]}
                    label={t("productAutomation.hidePriceWhenPreSaleLinkExists")}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    hidden={hiddenField}
                    name={["cart", "customButtonLabel"]}
                    label={t("productAutomation.customButtonLabel")}
                  >
                    <Input placeholder={t<string>("productAutomation.customButtonLabelPlaceholder")} />
                  </Form.Item>

                  <Form.Item
                    name="showTermsPP"
                    label={t<string>("productAutomation.customTerms")}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                  {showTermsPP && (
                    <>
                      <Form.Item
                        name="termsUrl"
                        label={t<string>("settings:checkout.termsUrl")}
                        rules={[
                          {
                            type: "url",
                            message: t<string>("common:validationErrors.wrongUrl"),
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        name="privacyPolicyUrl"
                        label={t<string>("settings:checkout.privacyPolicyUrl")}
                        rules={[
                          {
                            type: "url",
                            message: t<string>("common:validationErrors.wrongUrl"),
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item name="claim" label={t<string>("settings:checkout.claim")}>
                        <Input.TextArea rows={8} />
                      </Form.Item>
                    </>
                  )}
                  <Typography.Title level={5}>{t("productAutomation.countries")}</Typography.Title>
                  <Form.Item name="hideCountryField" valuePropName="checked">
                    <Checkbox>{t<string>("productAutomation.hideCountryField")}</Checkbox>
                  </Form.Item>
                  <Alert message={t<string>("productAutomation.hideCountryInfo")} style={{ margin: "-10px 0 20px" }} />

                  <Typography.Title level={5}>{t("productAutomation.lastNames")}</Typography.Title>
                  <Form.Item name="showLastNameField">
                    <Radio.Group style={{ flexDirection: "column", display: "flex", marginTop: "0.5rem" }}>
                      <Radio value={LastNameFieldValues.Hidden}>{t("productAutomation.hideLastNameField")}</Radio>
                      <Radio value={LastNameFieldValues.ShowOptional}>
                        {t("productAutomation.showLastNameOptionalField")}
                      </Radio>
                      <Radio value={LastNameFieldValues.ShowRequired}>
                        {t("productAutomation.showLastNameRequiredField")}
                      </Radio>
                    </Radio.Group>
                  </Form.Item>

                  <Typography.Title level={5}>{t("productAutomation.phones")}</Typography.Title>
                  <Form.Item name="showPhoneField">
                    <Radio.Group style={{ flexDirection: "column", display: "flex", marginTop: "0.5rem" }}>
                      <Radio value={PhoneNumberFieldValues.Hidden}>{t("productAutomation.hidePhoneField")}</Radio>
                      <Radio value={PhoneNumberFieldValues.ShowOptional}>
                        {t("productAutomation.showPhoneOptionalField")}
                      </Radio>
                      <Radio value={PhoneNumberFieldValues.ShowRequired}>
                        {t("productAutomation.showPhoneRequiredField")}
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Alert message={t<string>("productAutomation.showPhoneInfo")} style={{ margin: "-10px 0 20px" }} />
                  <Typography.Title level={5}>{t("productAutomation.additionalInfo")}</Typography.Title>
                  <Form.Item name="showAdditionalInfoField">
                    <Radio.Group style={{ flexDirection: "column", display: "flex", marginTop: "0.5rem" }}>
                      <Radio value={AdditionalInfoField.Hidden}>{t("productAutomation.hideAdditionalInfoField")}</Radio>
                      <Radio value={AdditionalInfoField.ShowOptional}>
                        {t("productAutomation.showAdditionalInfoOptionalField")}
                      </Radio>
                      <Radio value={AdditionalInfoField.ShowRequired}>
                        {t("productAutomation.showAdditionalInfoRequiredField")}
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Alert
                    message={t<string>("productAutomation.showAdditionalInfoTooltip")}
                    style={{ margin: "-10px 0 20px" }}
                  />

                  <Typography.Title level={5}>{t("productAutomation.payments")}</Typography.Title>

                  <Form.Item
                    name={["cart", "useTPayBlik"]}
                    label={t("productAutomation.useTPayBlik")}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name={["cart", "useTPayBlikRecurring"]}
                    label={t("productAutomation.useTPayBlikRecurring")}
                    tooltip={t<string>("productAutomation.useTPayBlikRecurringTooltip")}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name={["cart", "installmentsContractFraming"]}
                    label={t("productAutomation.installmentsContractFraming")}
                    tooltip={t<string>("productAutomation.installmentsContractFramingTooltip")}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name={["cart", "showTPayPayPoAsPaymentMethod"]}
                    label={t("productAutomation.allowTPayPayPo")}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name={["cart", "showTPayPayPalAsPaymentMethod"]}
                    label={t("productAutomation.allowTPayPayPal")}
                    tooltip={t<string>("productAutomation.allowTPayPayPalTooltip")}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name={["cart", "showTPayInstallments"]}
                    label={t("productAutomation.allowTPayInstallments")}
                    tooltip={t<string>("productAutomation.allowTPayInstallmentsTooltip")}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  {showTPayInstallments ? (
                    <>
                      <Form.Item
                        name={["cart", "tpayInstallmentsChannelIds"]}
                        label={t("productAutomation.tpayInstallmentsChannels")}
                        tooltip={t<string>("productAutomation.tpayInstallmentsChannelsTooltip")}
                        rules={[
                          {
                            required: true,
                            type: "array",
                            min: 1,
                            message: t<string>("productAutomation.tpayInstallmentsChannelRequired"),
                          },
                        ]}
                      >
                        <Select
                          allowClear
                          mode="multiple"
                          className="max-w-[350px]"
                          options={TPAY_INSTALLMENT_CHANNELS.map((channel) => ({
                            value: channel.channelId,
                            // Internal name (matches the TPay panel) — the checkout
                            // renders `customerLabel` or the override below instead.
                            label: channel.calculator
                              ? channel.label
                              : `${channel.label} — ${t("productAutomation.tpayInstallmentsNoCalculator")}`,
                          }))}
                        />
                      </Form.Item>

                      <Space align="start">
                        <Form.Item
                          name={["cart", "tpayInstallmentsMinAmount"]}
                          label={t("productAutomation.tpayInstallmentsMinAmount")}
                          tooltip={t<string>("productAutomation.tpayInstallmentsAmountRangeTooltip")}
                        >
                          <InputNumber min={0} step={100} addonAfter="zł" />
                        </Form.Item>

                        <Form.Item
                          name={["cart", "tpayInstallmentsMaxAmount"]}
                          label={t("productAutomation.tpayInstallmentsMaxAmount")}
                          tooltip={t<string>("productAutomation.tpayInstallmentsMaxAmountTooltip")}
                          dependencies={[["cart", "tpayInstallmentsMinAmount"]]}
                          rules={[
                            // An inverted range is accepted by every layer below and then
                            // matches no amount at all, so installments simply stop showing
                            // up in the checkout with nothing to explain why.
                            ({ getFieldValue }) => ({
                              validator: (_rule, value: number | null) => {
                                const min = getFieldValue(["cart", "tpayInstallmentsMinAmount"]) as number | null;

                                if (typeof value !== "number" || typeof min !== "number" || value >= min) {
                                  return Promise.resolve();
                                }

                                return Promise.reject(
                                  new Error(t<string>("productAutomation.tpayInstallmentsInvertedRange")),
                                );
                              },
                            }),
                          ]}
                        >
                          <InputNumber min={0} step={100} addonAfter="zł" />
                        </Form.Item>
                      </Space>

                      {selectedInstallmentChannels.length ? (
                        <>
                          <Typography.Text type="secondary">
                            {t("productAutomation.tpayInstallmentsLabelsHint")}
                          </Typography.Text>
                          {selectedInstallmentChannels.map((channel) => (
                            <Form.Item
                              key={channel.channelId}
                              name={["cart", "tpayInstallmentsChannelLabels", String(channel.channelId)]}
                              label={channel.label}
                              className="mt-2"
                            >
                              <Input className="max-w-[350px]" placeholder={channel.customerLabel} allowClear />
                            </Form.Item>
                          ))}
                        </>
                      ) : null}
                    </>
                  ) : null}
                </Card>
              </Tabs.TabPane>
            </>
          )}
        </Tabs>
      </Form>
    </Card>
  );
};

export default ProductSettings;
